import * as THREE from 'three';
import { FLIGHT, WARP } from '../config';
import type { FlightIntent } from '../core/Input';
import type { BodyState, Universe } from '../astro/Universe';
import type { ShipSpec } from './ShipCatalog';

export type SurfaceHeightFn = (body: BodyState, worldPos: THREE.Vector3) => number;

export type Landed = { bodyId: string; local: THREE.Vector3; localQuat: THREE.Quaternion };

export type ShipEvent =
  | { type: 'landed'; bodyId: string; speed: number }
  | { type: 'crash'; bodyId: string; speed: number }
  | { type: 'takeoff'; bodyId: string }
  | { type: 'warp'; state: 'spool' | 'on' | 'off'; reason?: string };

const FORWARD = new THREE.Vector3(0, 0, -1);
const UP = new THREE.Vector3(0, 1, 0);
const tmpA = new THREE.Vector3();
const tmpB = new THREE.Vector3();
const tmpQ = new THREE.Quaternion();
const tmpE = new THREE.Euler();

/** Arcade flight model in heliocentric float64 metres, dragged along by the dominant body. */
export class PlayerShip {
  readonly position = new THREE.Vector3();
  readonly velocity = new THREE.Vector3();
  readonly quaternion = new THREE.Quaternion();
  readonly angularVelocity = new THREE.Vector3(); // local pitch (x), yaw (y), roll (z) rad/s
  throttle = 0;
  boosting = false;
  landed: Landed | null = null;
  maxSpeed = 0;
  altitude = Infinity;
  nearestBody: BodyState | null = null;
  frameBody: BodyState | null = null;
  autopilotControl = false;
  shield = 100;
  maxShield = 100;
  lastDamageTime = -Infinity;
  dead = false;
  /** distance to the nearest obstacle surface (rocks, ships); set by the game each frame */
  proximity = Infinity;
  warp: 'off' | 'spool' | 'on' = 'off';
  warpCharge = 0;

  get clearance(): number {
    return this.spec.length * this.spec.frigate.height * 0.72;
  }

  /** Why the drive can't engage right now, or null if it can. */
  warpBlocked(): string | null {
    if (this.landed) return 'Cannot warp while landed';
    if (this.dead) return 'Ship destroyed';
    if (this.proximity < WARP.massLockDistance) return 'Mass-locked · move away from nearby objects';
    const b = this.nearestBody;
    if (b && b.def.kind !== 'star' && this.altitude < 20_000) return 'Too close to the surface · climb above 20 km';
    return null;
  }

  /** Toggle: off → spool, spool/on → off. Returns an event or a refusal reason. */
  toggleWarp(events: ShipEvent[]): string | null {
    if (this.warp !== 'off') {
      this.dropWarp(events, 'Warp disengaged');
      return null;
    }
    const blocked = this.warpBlocked();
    if (blocked) return blocked;
    this.warp = 'spool';
    this.warpCharge = 0;
    events.push({ type: 'warp', state: 'spool' });
    return null;
  }

  dropWarp(events: ShipEvent[], reason: string): void {
    if (this.warp === 'off') return;
    this.warp = 'off';
    this.warpCharge = 0;
    // Drop out instantly to normal-space speed.
    const normalMax = Math.max(FLIGHT.minMaxSpeed, Math.min(FLIGHT.capSpeed, Math.max(this.altitude, 1) * FLIGHT.speedPerAltitude));
    if (this.velocity.length() > normalMax) this.velocity.setLength(normalMax);
    this.throttle = Math.min(this.throttle, 0.5);
    events.push({ type: 'warp', state: 'off', reason });
  }

  constructor(public spec: ShipSpec) {
    this.resetShield();
  }

  resetShield(): void {
    this.maxShield = 60 + this.spec.shield * 90;
    this.shield = this.maxShield;
    this.dead = false;
  }

  /** Place at a world position with a given up and forward direction. */
  placeWorld(pos: THREE.Vector3, up: THREE.Vector3, forward: THREE.Vector3): void {
    this.position.copy(pos);
    const f = forward.clone().addScaledVector(up, -forward.dot(up)).normalize();
    const right = new THREE.Vector3().crossVectors(f, up).normalize();
    this.quaternion.setFromRotationMatrix(new THREE.Matrix4().makeBasis(right, up.clone().normalize(), f.negate()));
    this.velocity.set(0, 0, 0);
    this.angularVelocity.set(0, 0, 0);
    this.throttle = 0;
    this.landed = null;
  }

  get speed(): number {
    return this.velocity.length();
  }

  forward(out = new THREE.Vector3()): THREE.Vector3 {
    return out.copy(FORWARD).applyQuaternion(this.quaternion);
  }

  up(out = new THREE.Vector3()): THREE.Vector3 {
    return out.copy(UP).applyQuaternion(this.quaternion);
  }

  /** Place the ship above a body at lat/lon/altitude, level with the horizon, heading east. */
  placeAt(universe: Universe, body: BodyState, lat: number, lon: number, altitude: number): void {
    universe.surfacePoint(body, lat, lon, altitude, this.position);
    const up = tmpA.subVectors(this.position, body.position).normalize();
    const pole = new THREE.Vector3(0, 0, 1).applyMatrix4(new THREE.Matrix4().extractRotation(body.rotation));
    const east = new THREE.Vector3().crossVectors(pole, up).normalize();
    // Basis: -Z forward = east, +Y = up
    const m = new THREE.Matrix4().makeBasis(new THREE.Vector3().crossVectors(up, east.clone().negate()), up, east.clone().negate());
    this.quaternion.setFromRotationMatrix(m);
    this.velocity.set(0, 0, 0);
    this.angularVelocity.set(0, 0, 0);
    this.throttle = 0;
    this.landed = null;
  }

  update(dt: number, intent: FlightIntent, universe: Universe, heightFn: SurfaceHeightFn, events: ShipEvent[]): void {
    const spec = this.spec;
    // ---------- Landed: stay glued to the rotating surface ----------
    if (this.landed) {
      const body = universe.get(this.landed.bodyId);
      // Terrain data can sharpen after touchdown: stay on the current surface.
      this.position.copy(this.landed.local).applyMatrix4(body.rotation).add(body.position);
      const ground = body.def.radius + heightFn(body, this.position) + this.clearance;
      this.landed.local.setLength(ground);
      this.position.copy(this.landed.local).applyMatrix4(body.rotation).add(body.position);
      this.quaternion.copy(body.quaternion).multiply(this.landed.localQuat);
      this.velocity.set(0, 0, 0);
      this.throttle = Math.max(0, this.throttle + intent.throttleDelta * FLIGHT.throttleRate * dt);
      if (intent.strafeUp > 0.1 || this.throttle > 0.05) {
        events.push({ type: 'takeoff', bodyId: body.def.id });
        this.landed = null;
        this.velocity.copy(this.up(tmpA)).multiplyScalar(8);
      } else {
        this.updateAltitude(universe, heightFn);
        return;
      }
    }

    // ---------- Frame dragging ----------
    const frame = universe.dominantBody(this.position);
    this.frameBody = frame;
    this.position.add(frame.delta);
    const rel = tmpA.subVectors(this.position, frame.position);
    if (frame.def.kind !== 'star' && rel.length() - frame.def.radius < FLIGHT.corotateAltitude) {
      rel.applyQuaternion(frame.deltaRotation);
      this.position.copy(frame.position).add(rel);
      this.velocity.applyQuaternion(frame.deltaRotation);
      this.quaternion.premultiply(frame.deltaRotation);
    }

    // ---------- Rotation ----------
    // ---------- Warp drive ----------
    if (this.warp === 'spool') {
      const blocked = this.warpBlocked();
      if (blocked) this.dropWarp(events, blocked);
      else {
        this.warpCharge = Math.min(1, this.warpCharge + dt / WARP.spoolTime);
        if (this.warpCharge >= 1) {
          this.warp = 'on';
          events.push({ type: 'warp', state: 'on' });
        }
      }
    } else if (this.warp === 'on') {
      const b = this.nearestBody;
      if (this.proximity < WARP.massLockDistance * 0.75) this.dropWarp(events, 'Warp dropout · mass-locked');
      else if (b) {
        const toBody = tmpA.subVectors(b.position, this.position).normalize();
        const v = this.velocity.length();
        // Only a genuine approach counts (tangential orbit departures must not trip it).
        const closing = v > 1 && this.velocity.dot(toBody) / v > 0.35;
        const limit = b.def.radius * (b.def.kind === 'star' ? 4 : WARP.dropoutRadii);
        if (closing && this.altitude < limit) this.dropWarp(events, `Warp dropout · arriving at ${b.def.name}`);
      }
    }
    const inWarp = this.warp === 'on';

    const handling = (0.6 + spec.handling * 0.8) * (inWarp ? WARP.turnFactor : 1);
    const target = tmpB.set(
      intent.pitch * FLIGHT.pitchRate * handling,
      -intent.yaw * FLIGHT.yawRate * handling,
      -intent.roll * FLIGHT.rollRate * handling,
    );
    const k = 1 - Math.exp(-FLIGHT.angularResponse * handling * dt);
    this.angularVelocity.lerp(target, k);
    tmpE.set(this.angularVelocity.x * dt, this.angularVelocity.y * dt, this.angularVelocity.z * dt, 'XYZ');
    this.quaternion.multiply(tmpQ.setFromEuler(tmpE)).normalize();

    // ---------- Speed ----------
    if (!this.autopilotControl) {
      this.throttle += intent.throttleDelta * FLIGHT.throttleRate * dt;
      if (intent.fullStop) this.throttle = 0;
      this.throttle = Math.max(FLIGHT.reverseLimit, Math.min(1, this.throttle));
    }
    this.updateAltitude(universe, heightFn);
    const speedMul = 0.7 + spec.speed * 0.6;
    this.boosting = intent.boost && this.throttle > 0;
    // Mass-lock: nearby rocks and ships cap speed like a surface would.
    const alt = Math.max(inWarp ? this.altitude : Math.min(this.altitude, this.proximity + spec.length * 4), 1);
    this.maxSpeed = inWarp
      ? Math.min(WARP.cap, Math.max(FLIGHT.minMaxSpeed, alt * WARP.speedPerAltitude)) * speedMul
      : Math.min(FLIGHT.capSpeed, Math.max(FLIGHT.minMaxSpeed, alt * FLIGHT.speedPerAltitude)) * speedMul;
    if (inWarp && !this.autopilotControl) this.throttle = Math.max(this.throttle, 1);
    const targetSpeed = this.throttle * this.maxSpeed * (this.boosting ? FLIGHT.boostMultiplier : 1);
    const targetVel = this.forward(tmpA).multiplyScalar(targetSpeed);
    // Landing assist: descending with F slows near the ground so touchdown stays under the crash limit.
    const strafe = intent.strafeUp < 0 ? Math.min(FLIGHT.strafeSpeed, Math.max(4, (this.altitude - this.clearance) * 0.8)) : FLIGHT.strafeSpeed;
    targetVel.addScaledVector(this.up(tmpB), intent.strafeUp * strafe);
    // Hovering just above the ground with no input settles gently onto it.
    if (this.altitude - this.clearance < 10 && Math.abs(this.throttle) < 0.02 && intent.strafeUp === 0 && this.nearestBody?.def.kind !== 'star') {
      targetVel.addScaledVector(this.up(tmpB), -2);
    }
    const slowing = targetVel.lengthSq() < this.velocity.lengthSq();
    const tau = intent.fullStop ? 0.25 : slowing ? 0.3 : FLIGHT.accelTime;
    this.velocity.lerp(targetVel, 1 - Math.exp(-dt / tau));
    // Safety: never cover more than half the remaining altitude in one frame.
    const step = this.velocity.length() * dt;
    if (step > alt * 0.5 && this.nearestBody) {
      const toBody = tmpB.subVectors(this.nearestBody.position, this.position).normalize();
      if (this.velocity.dot(toBody) > 0) this.velocity.multiplyScalar((alt * 0.5) / step);
    }

    this.position.addScaledVector(this.velocity, dt);

    // ---------- Surface contact ----------
    this.updateAltitude(universe, heightFn);
    const body = this.nearestBody!;
    const clearance = this.clearance;
    if (this.altitude < clearance) {
      const up = tmpA.subVectors(this.position, body.position).normalize();
      const impact = this.velocity.length();
      // push back onto the surface
      this.position.addScaledVector(up, clearance - this.altitude);
      if (this.velocity.dot(up) > 0.5 && body.def.kind !== 'star') {
        // Climbing away (e.g. just lifted off): not a contact.
        this.updateAltitude(universe, heightFn);
        return;
      }
      if (body.def.kind === 'star' || impact > FLIGHT.landingMaxSpeed) {
        events.push({ type: 'crash', bodyId: body.def.id, speed: impact });
        this.velocity.set(0, 0, 0);
      } else {
        this.land(body, up);
        events.push({ type: 'landed', bodyId: body.def.id, speed: impact });
      }
      this.updateAltitude(universe, heightFn);
    }
  }

  private land(body: BodyState, up: THREE.Vector3): void {
    // Level the ship on the ground, keeping its heading.
    const fwd = this.forward(tmpB);
    fwd.addScaledVector(up, -fwd.dot(up)).normalize();
    if (fwd.lengthSq() < 1e-6) fwd.set(1, 0, 0).addScaledVector(up, -up.x).normalize();
    const right = new THREE.Vector3().crossVectors(fwd, up);
    const m = new THREE.Matrix4().makeBasis(right, up, fwd.clone().negate());
    this.quaternion.setFromRotationMatrix(m);
    const inv = new THREE.Matrix4().copy(body.rotation).invert();
    this.landed = {
      bodyId: body.def.id,
      local: this.position.clone().sub(body.position).applyMatrix4(inv),
      localQuat: body.quaternion.clone().invert().multiply(this.quaternion),
    };
    this.velocity.set(0, 0, 0);
    this.angularVelocity.set(0, 0, 0);
    this.throttle = 0;
  }

  private updateAltitude(universe: Universe, heightFn: SurfaceHeightFn): void {
    const near = universe.nearestSurface(this.position);
    this.nearestBody = near.body;
    this.altitude = near.altitude - heightFn(near.body, this.position);
  }
}
